<template>
  <div class="space-y-4 max-w-xl mx-auto pb-4 sm:pb-8 w-full">
    <!-- Top Step Actions (Skip Step) -->
    <div v-if="canSkip" class="flex justify-end">
      <v-btn text small :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg text-xs font-bold capitalize" @click="skipStep">
        Skip Step
        <v-icon right x-small>mdi-chevron-double-right</v-icon>
      </v-btn>
    </div>

    <!-- Task Customer Badge -->
    <div v-if="taskData"
      class="bg-indigo-50/60 rounded-xl p-3 border border-indigo-100 flex items-center justify-between text-xs">
      <div>
        <span class="text-gray-500 block">Customer / Account</span>
        <span class="font-bold text-indigo-950 text-sm">{{ customerName }}</span>
      </div>
      <span class="bg-white text-indigo-700 font-extrabold px-2.5 py-1 rounded-lg border border-indigo-200">
        {{ caseNumber }}
      </span>
    </div>

    <!-- Existing Audio Playback -->
    <div v-if="existingAudioUrl && !isRecording && !audioBlob"
      class="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 text-center space-y-4">
      <div
        class="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto border border-emerald-100">
        <v-icon size="36" color="emerald darken-1">mdi-microphone-check</v-icon>
      </div>
      <div>
        <h3 class="text-sm font-bold text-gray-900 mb-1">Audio Recording Uploaded</h3>
        <p class="text-xs text-gray-500 mb-3">Audio note has been recorded for this visit.</p>
        <audio :src="existingAudioUrl" controls class="w-full"></audio>
      </div>

      <div class="flex items-center gap-3 pt-2">
        <v-btn outlined :color="$vuetify.theme.currentTheme.employeePrimary" class="flex-1 rounded-xl h-10 text-xs font-bold" @click="resetToRecord">
          <v-icon left small>mdi-microphone-outline</v-icon>
          Record Again
        </v-btn>
        <v-btn depressed :color="$vuetify.theme.currentTheme.employeePrimary" class="flex-1 white--text rounded-xl h-10 text-xs font-bold shadow-xs"
          @click="proceedToNext">
          Proceed
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Live Recording Recorder View -->
    <div v-else class="bg-white rounded-2xl p-4 sm:p-6 shadow-sm border border-gray-100 text-center space-y-4">
      <div
        class="w-20 h-20 sm:w-24 sm:h-24 rounded-full mx-auto flex items-center justify-center transition-all shadow-md"
        :class="isRecording ? 'bg-rose-500 text-white animate-pulse' : 'bg-indigo-50 text-indigo-600'">
        <v-icon size="40" :color="isRecording ? 'white' : 'indigo'">
          {{ isRecording ? 'mdi-microphone' : 'mdi-microphone-outline' }}
        </v-icon>
      </div>

      <div>
        <span class="font-mono text-xl sm:text-2xl font-extrabold text-gray-900">{{ formattedTimer }}</span>
        <p class="text-[11px] sm:text-xs text-gray-500 mt-1 mb-0">
          {{ recordingStatusText }}
        </p>
      </div>

      <!-- Preview Recorded Audio -->
      <div v-if="audioUrl && !isRecording" class="pt-1">
        <audio :src="audioUrl" controls class="w-full"></audio>
      </div>

      <!-- Recording Controls -->
      <div class="flex flex-col gap-2 pt-1">
        <template v-if="isRecording">
          <div class="flex items-center gap-2">
            <v-btn v-if="!isPaused" depressed small color="amber darken-2"
              class="flex-1 rounded-lg h-9 sm:h-10 text-[11px] sm:text-xs font-bold white--text"
              @click="pauseRecording">
              <v-icon left x-small>mdi-pause</v-icon>
              Pause
            </v-btn>
            <v-btn v-else depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
              class="flex-1 rounded-lg h-9 sm:h-10 text-[11px] sm:text-xs font-bold white--text"
              @click="resumeRecording">
              <v-icon left x-small>mdi-play</v-icon>
              Resume
            </v-btn>

            <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
              class="flex-1 rounded-lg h-9 sm:h-10 text-[11px] sm:text-xs font-bold white--text" @click="stopRecording">
              <v-icon left x-small>mdi-stop</v-icon>
              Stop & Save
            </v-btn>
          </div>
        </template>

        <template v-else-if="!audioBlob">
          <v-btn depressed block small :color="$vuetify.theme.currentTheme.employeePrimary"
            class="rounded-lg h-9 sm:h-10 text-[11px] sm:text-xs font-extrabold white--text shadow-xs"
            @click="startRecording">
            <v-icon left x-small>mdi-record-circle-outline</v-icon>
            Start Audio Recording
          </v-btn>
        </template>

        <template v-else>
          <div class="flex items-center gap-2">
            <v-btn outlined small color="grey darken-2"
              class="flex-1 rounded-lg h-9 sm:h-10 text-[11px] sm:text-xs font-bold" @click="resetToRecord">
              <v-icon left x-small>mdi-refresh</v-icon>
              Discard
            </v-btn>

            <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary"
              class="flex-1 rounded-lg h-9 sm:h-10 text-[11px] sm:text-xs font-bold white--text shadow-xs"
              :loading="uploadingAudio" @click="uploadRecording">
              <v-icon left x-small>mdi-cloud-upload</v-icon>
              Upload
            </v-btn>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getTaskById,
  getCollectionCaseById,
  uploadTaskAudio,
  getTaskSettings,
  getOrgConfig
} from '@/app/services/dashboard'
import {
  sendStepLocation,
  computeTaskSteps,
  getNextStepRoute
} from '@/app/utils/taskFlow'

export default {
  name: 'AppTaskAudio',
  data() {
    return {
      taskId: this.$route.params.taskId,
      isCollection: this.$route.query.isCollection === 'true' || this.$route.query.isCollection === true,
      taskData: null,
      taskSettings: null,
      orgConfig: null,
      stepsList: [],
      mediaRecorder: null,
      audioChunks: [],
      audioBlob: null,
      audioUrl: null,
      isRecording: false,
      isPaused: false,
      timerSeconds: 0,
      timerInterval: null,
      uploadingAudio: false
    }
  },
  computed: {
    customerName() {
      return (
        this.taskData?.customer?.customerFullName ||
        this.taskData?.customer?.name ||
        this.taskData?.taskTitle ||
        'Customer'
      )
    },
    caseNumber() {
      return this.taskData?.caseNo ? `#${this.taskData.caseNo}` : 'Task'
    },
    currentStepIndex() {
      const idx = this.stepsList.findIndex((s) => s.routeName === 'AppTaskAudio')
      return idx >= 0 ? idx : 2
    },
    totalStepsCount() {
      return this.stepsList.length || 3
    },
    canSkip() {
      const current = this.stepsList.find((s) => s.routeName === 'AppTaskAudio')
      return !!current?.canSkip
    },
    existingAudioUrl() {
      const path = this.taskData?.additionalInfo?.audio?.doc_physical_path || this.taskData?.additionalInfo?.audio
      if (!path || typeof path !== 'string') return null
      if (path.startsWith('http')) return path
      const s3Base = process.env.VUE_APP_S3_URL || 'https://finfi-prod.s3.ap-south-1.amazonaws.com'
      return `${s3Base}/${path}`
    },
    formattedTimer() {
      const m = Math.floor(this.timerSeconds / 60).toString().padStart(2, '0')
      const s = (this.timerSeconds % 60).toString().padStart(2, '0')
      return `${m}:${s}`
    },
    recordingStatusText() {
      if (this.isRecording) {
        return this.isPaused ? 'Recording paused' : 'Recording in progress...'
      }
      return 'Tap start below to begin recording meeting discussion'
    }
  },
  mounted() {
    this.initStep()
  },
  beforeDestroy() {
    if (this.timerInterval) clearInterval(this.timerInterval)
    if (this.mediaRecorder && this.isRecording) {
      try {
        this.mediaRecorder.stop()
      } catch (e) {
        console.warn(e)
      }
    }
  },
  methods: {
    async initStep() {
      try {
        const [taskRes, settingsRes, orgRes] = await Promise.allSettled([
          getTaskById({ taskId: this.taskId, isCollection: this.isCollection })
            .catch(() => (this.isCollection ? getCollectionCaseById({ caseId: this.taskId }) : null)),
          getTaskSettings(),
          getOrgConfig()
        ])

        if (taskRes.status === 'fulfilled' && taskRes.value) {
          const resData =
            taskRes.value.data?.case ||
            taskRes.value.data?.task ||
            taskRes.value.data?.data?.case ||
            taskRes.value.data?.data?.task ||
            taskRes.value.data?.data ||
            taskRes.value.data
          this.taskData = resData
        }

        if (settingsRes.status === 'fulfilled') {
          this.taskSettings = settingsRes.value.data?.data || settingsRes.value.data
        }

        if (orgRes.status === 'fulfilled') {
          this.orgConfig = orgRes.value.data?.data || orgRes.value.data
        }

        this.stepsList = computeTaskSteps(this.taskSettings, this.orgConfig)
      } catch (err) {
        console.error('Error initializing audio step:', err)
      }
    },
    async startRecording() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        this.mediaRecorder = new MediaRecorder(stream)
        this.audioChunks = []

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data)
          }
        }

        this.mediaRecorder.onstop = () => {
          this.audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' })
          this.audioUrl = URL.createObjectURL(this.audioBlob)
          stream.getTracks().forEach((track) => track.stop())
        }

        this.mediaRecorder.start(100)
        this.isRecording = true
        this.isPaused = false
        this.timerSeconds = 0

        this.timerInterval = setInterval(() => {
          if (!this.isPaused) {
            this.timerSeconds++
          }
        }, 1000)
      } catch (err) {
        console.error('Microphone permission error:', err)
        this.$toast?.error?.('Please allow microphone permissions to record audio.')
      }
    },
    pauseRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.pause()
        this.isPaused = true
      }
    },
    resumeRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
        this.mediaRecorder.resume()
        this.isPaused = false
      }
    },
    stopRecording() {
      if (this.mediaRecorder && (this.mediaRecorder.state === 'recording' || this.mediaRecorder.state === 'paused')) {
        this.mediaRecorder.stop()
        this.isRecording = false
        this.isPaused = false
        if (this.timerInterval) clearInterval(this.timerInterval)
      }
    },
    resetToRecord() {
      this.audioBlob = null
      this.audioUrl = null
      this.timerSeconds = 0
    },
    async uploadRecording() {
      if (!this.audioBlob) return
      try {
        this.uploadingAudio = true
        const validTaskId =
          this.taskData?._id ||
          this.taskData?.taskId ||
          this.taskData?.case?.tasks?.[0]?._id ||
          this.taskId

        const formData = new FormData()
        formData.append('attachments', this.audioBlob, 'audio.webm')
        formData.append('taskId', validTaskId)
        if (this.isCollection) {
          formData.append('caseType', 'collection')
        }

        await uploadTaskAudio(formData)
        this.$toast?.success?.('Audio recording uploaded!')

        await sendStepLocation('AppTaskAudio', this.taskData)
        this.proceedToNext()
      } catch (err) {
        console.error('Error uploading audio:', err)
        this.$toast?.error?.(err.response?.data?.message || 'Failed to upload audio')
      } finally {
        this.uploadingAudio = false
      }
    },
    skipStep() {
      this.proceedToNext()
    },
    proceedToNext() {
      const nextRouteName = getNextStepRoute('AppTaskAudio', this.stepsList, this.taskData)
      this.$router.push({
        name: nextRouteName,
        params: { taskId: this.taskId },
        query: { isCollection: this.isCollection }
      })
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>
